import React, { useState, useRef, ChangeEvent, DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { TRANSLATION_CONFIG } from "../../utils/consts/translation";
import { bytesToKilobytes, formatAllowedTypesForAccept, isAllowedType } from "../../utils/file";
import { CheckmarkOutline, CloudUpload, DocumentAttachment, TrashCan } from "@carbon/icons-react";
import { cn } from "../../utils/styles";

interface FileUploadProps {
  label: string;
  files: File[] | null;
  error?: string;
  allowMultiple?: boolean;
  setFiles: (value: string | File[] | null) => void;
  disabled: boolean;
  fileName?: string;
  allowedTypes: string;
}

export const FileUpload = ({
  label,
  allowMultiple,
  setFiles,
  files,
  disabled,
  fileName,
  allowedTypes,
}: FileUploadProps) => {
  const { t } = useTranslation(TRANSLATION_CONFIG.appNamespace);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const acceptType = formatAllowedTypesForAccept(allowedTypes);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFiles = Array.from(event.target.files);
      const filteredFiles = selectedFiles.filter((file) => isAllowedType(file, allowedTypes));
      let updatedFiles = allowMultiple ? [...(files || []), ...filteredFiles] : filteredFiles;

      // Remove duplicates (based on file name and size)
      updatedFiles = updatedFiles.filter(
        (file, index, self) =>
          index === self.findIndex((f) => f.name === file.name && f.size === file.size),
      );

      setFiles(updatedFiles);
    }
  };

  const handleRemoveFile = (index: number) => {
    if (files) {
      const updatedFiles = files.filter((_, i) => i !== index);
      setFiles(updatedFiles);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      const filteredFiles = droppedFiles.filter((file) => isAllowedType(file, allowedTypes));

      let updatedFiles = allowMultiple ? [...(files || []), ...filteredFiles] : filteredFiles;

      // Remove duplicates (based on file name and size)
      updatedFiles = updatedFiles.filter(
        (file, index, self) =>
          index === self.findIndex((f) => f.name === file.name && f.size === file.size),
      );

      setFiles(updatedFiles);
    }
  };

  return (
    <div className="flex flex-col gap-[26px]">
      {label && <p className="text-gray-600 font-medium">{label}</p>}
      <div className="flex gap-2 items-center">
        <DocumentAttachment size={18} className="text-ominimo-400" />
        <p className="text-[16px] leading-[140%] tracking-[0.2px] text-black">{fileName}</p>
      </div>
      <div
        className={`w-full border-2 rounded-md px-6 pb-8 pt-7 h-[200px] flex flex-col items-center justify-center ${
          dragging ? "border-purple-500" : "border-gray-200"
        } `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <input
          disabled={disabled}
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          multiple={allowMultiple ? true : false}
          accept={acceptType}
        />
        <div className="flex flex-col justify-center items-center gap-y-4">
          <CloudUpload size={24} className="text-gray-550" />
          <div className="flex flex-col justify-center items-center gap-y-2">
            <div className="flex flex-row justify-center items-baseline gap-x-[5px]">
              <p
                onClick={handleButtonClick}
                className="text-[16px] font-semibold leading-[20.16px] tracking-[0.2px] text-ominimo-400 cursor-pointer">
                {t("Click to upload")}
              </p>
              <p className="text-[16px] font-light leading-[20.16px] text-gray-375">
                {t("or drag and drop")}
              </p>
            </div>
            <p className="text-[16px] font-light leading-[20.16px] text-gray-375">
              {t("Allowed file types:")} {allowedTypes} (max. 5MB)
            </p>
          </div>
        </div>
      </div>
      <div className={cn(files?.[0] && "pb-7")}>
        {files?.[0] &&
          files?.map((file, index) => (
            <div
              key={index}
              className="flex items-start justify-start bg-ghost-110 rounded-md p-4 border border-gray-100 gap-3 mt-1">
              <div>
                <DocumentAttachment size={40} />
              </div>
              <div className="w-full">
                <div className="flex justify-between items-center w-full">
                  <p className="text-black truncate text-[16px] font-normal leading-[20.16px]">
                    {file.name}
                  </p>
                  <CheckmarkOutline className="text-ominimo-400" size={20} />
                </div>

                <p className="text-gray-550 truncate text-[15px] font-light leading-[18.9px]">
                  {bytesToKilobytes(file.size)} KB
                </p>
                <div className="flex justify-between items-center gap-2 w-full mt-1">
                  <div className="flex justify-between items-center gap-2 w-full">
                    <div className="h-2 rounded-lg bg-ominimo-400 flex-1"></div>
                    <div className="text-[15px] font-light leading-[18.9px] text-gray-550 flex-shrink-0">
                      100 %
                    </div>
                  </div>
                  <div className="ml-[10px]">
                    <TrashCan
                      className="cursor-pointer text-gray-400"
                      onClick={() => handleRemoveFile(index)}
                      size={16}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
